/**
 * Payouts per calendar month: the best and worst months, the pattern across
 * years, and the month-by-month series.
 *
 * Exists because the revenue skill answers a single total, and every question
 * containing "revenue" used to reach it — so "what months do I tend to have the
 * most revenue" was answered with a 12-month sum. One request to the payouts
 * breakdown returns every month at once, filled with zeros for quiet months,
 * which is the Payouts report's data narrowed to settled payouts.
 *
 * Wording follows the revenue skill: these are amounts *paid out*, net of
 * SecurSpace fees, not gross booking value. Only payouts that reached the
 * supplier are counted, so pending or failed payouts cannot make a month look
 * better than it was.
 */

import moment from 'moment';
import { formatMoneyAddDecimalPointAndThousands } from '../../util/PaymentUtils';
import { detectComparison, type ComparisonQuestion } from './comparisonQuestion';
import { classifyLookup, isHowToOrActionRequest } from './lookupRouting';
import { apiDateRange, type ApiDateRange } from './requests/apiDates';
import { fetchPayoutMonths, type PayoutMonthRow } from './requests/payout-breakdown-requests';
import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';

/** Matches the revenue skill's cap: one request per location, at most this many. */
const MAX_LOCATION_REQUESTS = 12;

/** Complete months looked back over for "which months tend to be best". */
const SEASONAL_LOOKBACK_MONTHS = 36;

/** Complete months looked back over when the question names no period. */
const DEFAULT_LOOKBACK_MONTHS = 12;

/** Below this much history, a "pattern" is really one year's numbers. */
const MIN_SEASONAL_MONTHS = 24;

/** Most months listed chronologically before the list keeps only the latest. */
const MAX_LISTED_MONTHS = 24;

/** Calendar months named in a seasonal answer, at each end. */
const SEASONAL_HIGHLIGHTS = 3;

/** Months averaged on each side of the "last 3 vs the 3 before" trend comparison. */
const TREND_SPAN = 3;

const MONTH_KEY = 'YYYY-MM';

const PAYOUTS_LINK: KnowledgeLink = { label: 'Open Payouts', path: '/supplier-payouts' };

/** Words that unambiguously ask about money paid to the supplier. */
const MONEY_WORDS =
  /\b(revenues?|earn|earns|earned|earning|earnings|income|payouts?|paid\s+out|(get|got|gets|getting|was|were|been|be)\s+paid|money)\b/i;

/** "Which month did I make the most" means money, unless it names something else to make. */
const MAKE_WORDS = /\b(make|makes|made|making)\b/i;
const OTHER_THINGS_MADE =
  /\b(bookings?|orders?|reservations?|spaces?|containers?|equipment|check[\s-]?ins?|check[\s-]?outs?|invoices?)\b/i;

/**
 * Money that is not a payout. Refunds, fees, overages and invoice totals come
 * from other endpoints; receivables and ready-for-payout are money not yet
 * paid out. All of those are left to the skills or agent that can read them.
 */
const OTHER_MONEY =
  /\b(refunds?|refunded|fees?|overages?|outstanding|owed?|owing|unpaid|receivables?|pending|ready\s+for\s+payout|invoices?|charges?|charged|spend|spent|costs?)\b/i;

const asksAboutPayouts = (message: string): boolean => {
  if (OTHER_MONEY.test(message)) return false;
  if (MONEY_WORDS.test(message)) return true;
  return MAKE_WORDS.test(message) && !OTHER_THINGS_MADE.test(message);
};

/**
 * Claims a supplier's month-by-month money question outright.
 *
 * A claim rather than synonyms, because the phrasings multiply — "which month
 * had my highest revenue", "best month for payouts", "when do I usually earn
 * the most" — and the revenue skill's bare "revenue" synonym would otherwise
 * catch most of them. Defers to {@link ./lookupRouting} first, like every
 * non-lookup claim, so a message naming a record still goes to its lookup.
 */
export const claimsRevenueByMonth = (message: string): boolean => {
  if (classifyLookup(message) !== null || isHowToOrActionRequest(message)) return false;
  return detectComparison(message)?.dimension === 'month' && asksAboutPayouts(message);
};

interface MonthTotal {
  /** `YYYY-MM`. */
  key: string;
  cents: number;
  payouts: number;
}

interface BreakdownWindow {
  range: ApiDateRange;
  /** Slots after a verb: "you were paid out $X {label}". */
  label: string;
}

const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

const money = (cents: number): string => formatMoneyAddDecimalPointAndThousands(cents);

const monthName = (key: string): string => moment(key, MONTH_KEY, true).format('MMMM YYYY');

const calendarMonthName = (index: number): string => moment().month(index).format('MMMM');

/** Joins names as "A", "A and B" or "A, B and C". */
const listOf = (items: string[]): string =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * The `count` complete calendar months before the current one, plus the current
 * month so far when `throughToday` is set.
 */
const lookback = (count: number, throughToday: boolean): ApiDateRange =>
  apiDateRange({
    startDate: moment().subtract(count, 'months').startOf('month').format('YYYY-MM-DD'),
    endDate: (throughToday ? moment() : moment().subtract(1, 'month').endOf('month')).format('YYYY-MM-DD'),
  });

/**
 * The window to break down. A period the user named is honoured as asked; with
 * none, whole months — the revenue skill's rolling "last 12 months" starts
 * mid-month, and a half month would always look like the worst. The current
 * month is fetched so it can be listed "(so far)", but is never ranked; a
 * seasonal average leaves it out altogether.
 */
const windowFor = (context: KnowledgeContext, comparison: ComparisonQuestion): BreakdownWindow => {
  if (context.period.explicit) {
    return { range: apiDateRange(context.period), label: context.period.label };
  }
  if (comparison.shape === 'seasonal') {
    return {
      range: lookback(SEASONAL_LOOKBACK_MONTHS, false),
      label: `over the last ${SEASONAL_LOOKBACK_MONTHS / 12} years`,
    };
  }
  return {
    range: lookback(DEFAULT_LOOKBACK_MONTHS, true),
    label: `over the last ${DEFAULT_LOOKBACK_MONTHS} full months`,
  };
};

const toTotals = (rows: PayoutMonthRow[]): Map<string, MonthTotal> => {
  const totals = new Map<string, MonthTotal>();
  for (const row of rows) {
    const key = row.group;
    if (!key || !moment(key, MONTH_KEY, true).isValid()) continue;
    const existing = totals.get(key) ?? { key, cents: 0, payouts: 0 };
    existing.cents += row.totalPayouts ?? 0;
    existing.payouts += row.payoutsCount ?? 0;
    totals.set(key, existing);
  }
  return totals;
};

const sorted = (totals: Map<string, MonthTotal>): MonthTotal[] =>
  Array.from(totals.values()).sort((a, b) => a.key.localeCompare(b.key));

interface Fetched {
  months: MonthTotal[];
  scopePhrase: string;
  /** Set when a location subset was too large to break down and the account was used instead. */
  widenedFrom?: number;
}

/**
 * Every month in the window, oldest first, for the locations asked about.
 *
 * The endpoint filters by one location or none, so a subset is one request per
 * location, summed month by month — capped like the revenue skill, and widened
 * to the whole account (and said so) beyond the cap.
 */
const fetchMonths = async (context: KnowledgeContext, range: ApiDateRange): Promise<Fetched> => {
  const { targets, wholeAccount } = context;

  if (!wholeAccount && targets.length === 1) {
    const rows = await fetchPayoutMonths({ ...range, locationId: targets[0].id });
    return { months: sorted(toTotals(rows)), scopePhrase: `for **${targets[0].name}**` };
  }

  if (!wholeAccount && targets.length > 1 && targets.length <= MAX_LOCATION_REQUESTS) {
    const pages = await Promise.all(
      targets.map((target) => fetchPayoutMonths({ ...range, locationId: target.id })),
    );
    return {
      months: sorted(toTotals(pages.flat())),
      scopePhrase: `across those ${pluralize(targets.length, 'location')}`,
    };
  }

  const rows = await fetchPayoutMonths(range);
  return {
    months: sorted(toTotals(rows)),
    scopePhrase: 'across all your locations',
    widenedFrom: !wholeAccount && targets.length > MAX_LOCATION_REQUESTS ? targets.length : undefined,
  };
};

/**
 * Drops the empty months before the first payout. A supplier who joined eight
 * months ago did not have four "worst months" of $0 — they had not started.
 */
const fromFirstPayout = (months: MonthTotal[]): MonthTotal[] => {
  const first = months.findIndex((month) => month.cents !== 0 || month.payouts > 0);
  return first === -1 ? [] : months.slice(first);
};

const average = (values: number[]): number =>
  values.length === 0 ? 0 : Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);

const widenedNote = (fetched: Fetched): string =>
  fetched.widenedFrom
    ? ` You asked about ${fetched.widenedFrom} locations, which is more than the ` +
      `${MAX_LOCATION_REQUESTS} I will break down at once, so these figures cover every location.`
    : '';

/** Best and worst of one window, the average, and optionally the direction. */
const describeTimeline = (
  months: MonthTotal[],
  comparison: ComparisonQuestion,
  span: BreakdownWindow,
  scopePhrase: string,
): KnowledgeAnswer => {
  const currentKey = moment().format(MONTH_KEY);
  const inProgress = (month: MonthTotal): boolean => month.key === currentKey;
  // A month still under way would always rank as the worst, so it is listed
  // but kept out of the comparison — unless it is all there is.
  const complete = months.filter((month) => !inProgress(month));
  const ranked = complete.length > 0 ? complete : months;

  const best = ranked.reduce((top, month) => (month.cents > top.cents ? month : top));
  const worst = ranked.reduce((low, month) => (month.cents < low.cents ? month : low));
  const sentences: string[] = [];

  const describe = (month: MonthTotal): string =>
    `**${monthName(month.key)}**, with **${money(month.cents)}** paid out across ` +
    pluralize(month.payouts, 'payout');

  if (ranked.length === 1) {
    sentences.push(`Only one month ${scopePhrase} ${span.label} had payouts: ${describe(best)}.`);
  } else {
    const bestLine = `Your best month ${scopePhrase} ${span.label} was ${describe(best)}.`;
    const worstLine =
      worst.cents === 0
        ? `Nothing was paid out in **${monthName(worst.key)}**.`
        : `Your slowest was ${describe(worst)}.`;
    if (comparison.wantsLowest) {
      sentences.push(
        worst.cents === 0
          ? `Your slowest month ${scopePhrase} ${span.label} was **${monthName(worst.key)}**, ` +
              'when nothing was paid out.'
          : `Your slowest month ${scopePhrase} ${span.label} was ${describe(worst)}.`,
        `Your best was ${describe(best)}.`,
      );
    } else {
      sentences.push(bestLine, worstLine);
    }
    sentences.push(
      `That averages **${money(average(ranked.map((month) => month.cents)))}** a month over ` +
        `${pluralize(ranked.length, 'month')}.`,
    );
  }

  if (comparison.shape === 'trend') sentences.push(describeTrend(complete));

  const listed = months.slice(-MAX_LISTED_MONTHS);
  if (listed.length < months.length) {
    sentences.push(`Listing the latest ${MAX_LISTED_MONTHS} months.`);
  }

  return {
    text: sentences.join(' '),
    breakdown: listed.map((month) => ({
      label: inProgress(month) ? `${monthName(month.key)} (so far)` : monthName(month.key),
      value: money(month.cents),
    })),
    link: PAYOUTS_LINK,
  };
};

/** The latest few complete months against the few before them. */
const describeTrend = (complete: MonthTotal[]): string => {
  if (complete.length < TREND_SPAN * 2) {
    return `There are not yet ${TREND_SPAN * 2} full months of payouts to show a direction.`;
  }
  const recent = average(complete.slice(-TREND_SPAN).map((month) => month.cents));
  const prior = average(complete.slice(-TREND_SPAN * 2, -TREND_SPAN).map((month) => month.cents));
  const lead = `Your last ${TREND_SPAN} full months averaged **${money(recent)}** a month`;

  if (prior === 0) {
    return recent === 0
      ? `Nothing was paid out in the last ${TREND_SPAN * 2} full months.`
      : `${lead}, up from nothing in the ${TREND_SPAN} months before.`;
  }
  const change = Math.round(((recent - prior) / prior) * 100);
  if (change === 0) {
    return `${lead}, level with the ${TREND_SPAN} months before (${money(prior)}).`;
  }
  return (
    `${lead}, ${change > 0 ? 'up' : 'down'} ${Math.abs(change)}% on the ` +
    `${TREND_SPAN} months before (${money(prior)}).`
  );
};

interface CalendarMonth {
  index: number;
  average: number;
  years: number;
}

/**
 * Which calendar months tend to be strongest, averaging each across the years
 * it appears in, so a supplier with three Augusts and two Januarys is compared
 * like for like.
 */
const describeSeasonal = (
  months: MonthTotal[],
  comparison: ComparisonQuestion,
  span: BreakdownWindow,
  scopePhrase: string,
): KnowledgeAnswer => {
  const currentKey = moment().format(MONTH_KEY);
  const complete = months.filter((month) => month.key !== currentKey);
  const history = complete.length > 0 ? complete : months;

  const buckets = new Map<number, number[]>();
  for (const month of history) {
    const index = moment(month.key, MONTH_KEY, true).month();
    buckets.set(index, [...(buckets.get(index) ?? []), month.cents]);
  }

  const calendar: CalendarMonth[] = Array.from(buckets.entries())
    .map(([index, values]) => ({ index, average: average(values), years: values.length }))
    .sort((a, b) => b.average - a.average || a.index - b.index);

  const highlight = Math.min(SEASONAL_HIGHLIGHTS, Math.floor(calendar.length / 2));
  const named = (entries: CalendarMonth[]): string =>
    listOf(entries.map((entry) => `**${calendarMonthName(entry.index)}** (${money(entry.average)})`));

  const top = calendar.slice(0, Math.max(highlight, 1));
  const bottom = highlight > 0 ? calendar.slice(-highlight).reverse() : [];
  const averaged = history.length >= MIN_SEASONAL_MONTHS ? ' on average' : '';

  const strongLine = `Your strongest ${top.length === 1 ? 'month tends' : 'months tend'} to be ${named(top)}${averaged}.`;
  const slowLine =
    bottom.length > 0
      ? `Your slowest ${bottom.length === 1 ? 'tends' : 'tend'} to be ${named(bottom)}.`
      : '';

  const first = monthName(history[0].key);
  const last = monthName(history[history.length - 1].key);
  const basis =
    history.length >= MIN_SEASONAL_MONTHS
      ? `Based on ${pluralize(history.length, 'month')} of payouts ${scopePhrase}, ${first} to ` +
        `${last}, with each calendar month averaged across the years it appears in.`
      : `That is based on only ${pluralize(history.length, 'month')} of payouts ${scopePhrase} ` +
        `(${first} to ${last}), so most months have a single year behind them — read it as ` +
        'one year\u2019s numbers rather than a settled seasonal pattern.';

  const lines = comparison.wantsLowest ? [slowLine, strongLine] : [strongLine, slowLine];
  const breakdown: KnowledgeBreakdownRow[] = calendar.map((entry) => ({
    label: calendarMonthName(entry.index),
    value: `${money(entry.average)}${entry.years > 1 ? ` avg over ${entry.years} years` : ''}`,
  }));

  return {
    text: [...lines.filter(Boolean), basis].join(' '),
    breakdown,
    link: PAYOUTS_LINK,
  };
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  // Reached by claim, synonym or planner; a planner may pick this skill for a
  // phrasing the detector does not read, so fall back to a ranking.
  const comparison: ComparisonQuestion = detectComparison(context.message) ?? {
    dimension: 'month',
    shape: 'rank',
    wantsLowest: false,
  };
  const span = windowFor(context, comparison);
  const fetched = await fetchMonths(context, span.range);
  const months = fromFirstPayout(fetched.months);

  if (months.length === 0) {
    return {
      text: `Nothing was paid out ${fetched.scopePhrase} ${span.label}, so there are no months to compare.`,
      link: PAYOUTS_LINK,
    };
  }

  const result =
    comparison.shape === 'seasonal'
      ? describeSeasonal(months, comparison, span, fetched.scopePhrase)
      : describeTimeline(months, comparison, span, fetched.scopePhrase);

  return { ...result, text: `${result.text}${widenedNote(fetched)}` };
};

export const revenueByMonthQuery: KnowledgeQuery = {
  id: 'revenueByMonth',
  audiences: ['supplier'],
  synonyms: [
    'revenue by month',
    'revenue month by month',
    'payouts by month',
    'monthly revenue',
    'best month for revenue',
  ],
  claims: claimsRevenueByMonth,
  example: 'what months do I tend to have the most revenue',
  description:
    'Money paid out to the supplier compared month by month, net of SecurSpace fees: ' +
    'which month was best or worst in a period, which calendar months tend to be ' +
    'strongest across years (seasonality), whether payouts are trending up or down, ' +
    'and the month-by-month list. Not a single total — that is the revenue skill.',
  answer,
};

export const __testing = {
  asksAboutPayouts,
  fromFirstPayout,
  MAX_LOCATION_REQUESTS,
  SEASONAL_LOOKBACK_MONTHS,
};
